import { service } from '@ember/service';
import AuthenticatedRoute from 'eflex/util/authenticated-route';
import pProps from 'p-props';
import JobStatuses from 'eflex/constants/kinetic-job-statuses';
import { jemViewModeKey } from 'eflex/constants/jem';

class JemStationsRoute extends AuthenticatedRoute {
  @service store;
  @service webCam;
  @service intl;
  @service notifier;
  @service router;
  @service currentUser;
  @service logRepo;
  @service configurationHistoryRepo;
  @service jemRepo;
  @service productionScheduleRepo;
  @service codeRepo;

  model({ id: stationId }) {
    const station = stationId != null ? this.store.peekRecord('station', stationId) : null;

    if (!station) {
      if (stationId != null) {
        const error = this.intl.t('errors.locationNotFound', { id: stationId });
        this.notifier.sendError(error);
      }
      this.router.transitionTo('jem');
      return;
    } else if (
      !this.currentUser.user.hasAuthorization(station.jemAuthorizedTags) &&
      !this.currentUser.user.hasConfigAuthorization(station.configEditorTags) &&
      this.currentUser.user.isUser
    ) {
      localStorage.removeItem('jemSelectedTreeItem');
      this.router.transitionTo('jem');
      return;
    }

    return pProps({
      webCams: this.webCam.load.perform(),
      station,
      jemNotes: this.store.query('jem-note', { stationId: station.id }),
      liveBuildStatuses: this.store.query('live-build-status', { station: station.id, limit: 1 }),
      productionSchedule: this.productionScheduleRepo.getCurrent(station),
      configurationHistories: this.configurationHistoryRepo
        .fetchConfigurationHistoriesForJem.perform({ stationId: station.id }),
    });
  }

  afterModel({ liveBuildStatuses, station }) {
    const promises = {};

    if (!station.usesModels && !station.usesOperations) {
      promises.componentTaskConfigs = this.jemRepo.loadTaskConfigs.perform(station);
    }

    if (station.usesOperations) {
      promises.jobs = this.store.query('kinetic-job', { jobStatus: JobStatuses.released });
      promises.rejectCodes = this.codeRepo.retrieveCodes('reject', { isFromSync: true });
      promises.scrapCodes = this.codeRepo.retrieveCodes('scrap', { isFromSync: true });
    }

    if (station.isMultipiece) {
      promises.buildStatusMultipieces = this.store.query('build-status-multipiece', {});
      promises.rejectCodes = this.codeRepo.retrieveCodes('reject', { station });
      promises.scrapCodes = this.codeRepo.retrieveCodes('scrap', { station });
    }

    const liveBuildStatus = liveBuildStatuses[0];
    if (liveBuildStatus) {
      promises.runningTaskConfigs = this.jemRepo.loadTaskConfigs.perform(
        station,
        liveBuildStatus.kineticOperationRecord ?? liveBuildStatus.modelRecord,
      );
    }

    return pProps(promises);
  }

  setupController(controller, { liveBuildStatuses, station, configurationHistories }) {
    const liveBuildStatus = liveBuildStatuses[0];

    Object.assign(controller, {
      station,
      configurationHistories,
      totalConfigurationHistories: configurationHistories.meta?.count ?? 0,
      buildStatus: liveBuildStatus,
    });

    const storedViewMode = localStorage.getItem(jemViewModeKey);
    if (storedViewMode != null) {
      Object.assign(controller, {
        viewMode: parseInt(storedViewMode),
      });
    }

    controller.setInstructionTab();
  }

  resetController(controller, isExiting, transition) {
    Object.assign(controller, {
      partContained: false,
      prereqsNotMet: false,
      tempSerialNumber: '',
      bomSourceLookupValue: null,
      selectedJob: null,
      selectedAssembly: null,
      kineticAssemblyOperation: null,
      currentTask: null,
      model: null,
    });

    if (isExiting) {
      if (transition.to.name !== 'oee.station') {
        this.store.unloadAll('oee-state');
      }
      controller.clearBuildStatus();
    }
  }
}

export default JemStationsRoute;
